import { useEffect } from "react";
import { toast } from "sonner";
import { loadFavorites, saveFavorites } from "./Favorites";
import { getSongsCached } from "../lib/songMatch";

// Module-level guard: survives React StrictMode double-invoke in dev.
// If the app is fully reloaded, this resets and the import prompt can re-run.
let alreadyProcessed = false;

/**
 * FavoritesImporter — reads a `?fav=code1,code2,...` URL param and offers
 * to import those tracks into the local favorites list. Renders nothing.
 */
export default function FavoritesImporter() {
  useEffect(() => {
    if (alreadyProcessed) return;
    if (typeof window === "undefined") return;
    const params = new URLSearchParams(window.location.search);
    const raw = params.get("fav");
    if (!raw) return;
    alreadyProcessed = true;

    const incoming = raw
      .split(",")
      .map((c) => c.trim())
      .filter(Boolean);

    // Clean URL so the prompt doesn't reappear on refresh
    try {
      const cleanUrl = new URL(window.location.href);
      cleanUrl.searchParams.delete("fav");
      window.history.replaceState({}, "", cleanUrl.toString());
    } catch { /* ignore */ }

    if (incoming.length === 0) return;

    const current = loadFavorites();
    const currentCodes = new Set(current.map((f) => f.code).filter(Boolean));
    const newCodes = incoming.filter((c) => !currentCodes.has(c));

    if (newCodes.length === 0) {
      toast.info("Preferiti già tutti presenti", {
        description: "Non c'è nulla di nuovo da importare.",
      });
      return;
    }

    const runImport = async () => {
      const loadingId = toast.loading("Importazione in corso…");
      const songs = await getSongsCached();
      const byCode = new Map(songs.map((s) => [s.code, s]));
      const added = [];
      for (const code of newCodes) {
        const s = byCode.get(code);
        if (!s) continue;
        const raw = `${s.artist} - ${s.title}`;
        added.push({ raw, code, addedAt: Date.now() });
      }
      toast.dismiss(loadingId);
      if (added.length === 0) {
        toast.error("Import fallito", {
          description: "Nessuno dei codici condivisi risulta in lista.",
        });
        return;
      }
      // Prepend new + keep existing, cap at 100
      const merged = [...added, ...loadFavorites()].slice(0, 100);
      saveFavorites(merged);
      toast.success(`Importati ${added.length} preferiti ♥`);
    };

    // Small delay to ensure <Toaster/> has mounted. No cleanup — this fires
    // once per app load and the component lives for the whole session.
    setTimeout(() => {
      toast(`${newCodes.length} preferiti pronti da importare`, {
        description: "Aggiungili alla tua lista?",
        duration: 15000,
        action: {
          label: "Importa",
          onClick: () => { runImport(); },
        },
      });
    }, 200);
  }, []);

  return null;
}
